import { useRef, useState, type RefObject } from 'react'
import { MAX_BPM, MIN_BPM, tapTempo } from '../../transport/tap'
import type { Phase } from '../../transport/transport'
import common from '../common.module.css'
import { t } from '../strings'
import styles from './Transport.module.css'

interface TransportBarProps {
  phase: Phase
  bpm: number
  referenceBpm: number | undefined
  metronome: boolean
  speedStep: number
  /** Nombre del bucle activo, o null. */
  loopLabel: string | null
  /** Se ha marcado el principio de un bucle A–B y falta el final. */
  pickingEnd: boolean
  /** El último fragmento trabajado, si no está activo. */
  fragmentLabel: string | null
  disabled: boolean
  beatRef: RefObject<HTMLSpanElement | null>
  barRef: RefObject<HTMLDivElement | null>
  onPlay: () => void
  onPause: () => void
  onStop: () => void
  onBpm: (bpm: number) => void
  onMetronome: () => void
  onLoopSection: () => void
  onLoopAB: () => void
  onClearLoop: () => void
  onFragment: () => void
  onComfortable: () => void
}

const clampBpm = (bpm: number) => Math.min(MAX_BPM, Math.max(MIN_BPM, Math.round(bpm)))

/**
 * La barra de la guía, abajo y con botones grandes: tocar y pausar, tempo, bucles, «Me sale» y el
 * contador de compás y pulso. Solo pinta y avisa; el transporte vive en Tocar.
 */
export function TransportBar({ beatRef, barRef, ...props }: TransportBarProps) {
  const p = t.transport
  const taps = useRef<number[]>([])
  // En pantallas pequeñas, lo secundario se pliega tras «Más».
  const [expanded, setExpanded] = useState(false)
  const playing = props.phase === 'playing' || props.phase === 'countIn'

  const tap = () => {
    const now = performance.now() / 1000
    taps.current = [...taps.current, now].slice(-12)
    const bpm = tapTempo(taps.current)
    if (bpm !== null) props.onBpm(bpm)
  }

  return (
    <div
      ref={barRef}
      className={styles.transport}
      data-expanded={expanded}
      role="toolbar"
      aria-label={p.label}
    >
      <div className={styles.group}>
        <button
          className={playing ? styles.pauseButton : styles.playButton}
          disabled={props.disabled}
          onClick={playing ? props.onPause : props.onPlay}
        >
          {playing ? `❚❚ ${p.pause}` : `▶ ${props.phase === 'paused' ? p.resume : p.play}`}
        </button>
        <button
          className={styles.square}
          disabled={props.phase === 'stopped'}
          onClick={props.onStop}
          aria-label={p.stopHint}
          title={p.stopHint}
        >
          ■
        </button>
        <span className={styles.position} ref={beatRef} aria-label={p.position} />
      </div>

      <div className={styles.group}>
        <button
          className={styles.square}
          aria-label={p.slower}
          onClick={() => props.onBpm(clampBpm(props.bpm - 1))}
        >
          −
        </button>
        <span className={styles.bpm}>{p.bpm(props.bpm)}</span>
        <button
          className={styles.square}
          aria-label={p.faster}
          onClick={() => props.onBpm(clampBpm(props.bpm + 1))}
        >
          +
        </button>
        <button
          className={styles.more}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {p.more(props.loopLabel !== null, props.metronome)}
        </button>
      </div>

      <div className={styles.secondary}>
        <button onPointerDown={tap} title={p.tapHint} aria-label={`${p.tap}. ${p.tapHint}`}>
          {p.tap}
        </button>
        {props.referenceBpm !== undefined && props.referenceBpm !== props.bpm && (
          <button onClick={() => props.onBpm(props.referenceBpm!)}>
            {p.original(props.referenceBpm)}
          </button>
        )}
      </div>

      <div className={styles.secondary}>
        {props.loopLabel ? (
          <button className={common.toggleOn} onClick={props.onClearLoop} title={p.clearLoop}>
            {p.loop(props.loopLabel)} ✕
          </button>
        ) : (
          <>
            <button disabled={props.disabled} onClick={props.onLoopSection}>
              {p.loopSection}
            </button>
            <button
              disabled={props.disabled}
              className={props.pickingEnd ? common.toggleOn : undefined}
              onClick={props.onLoopAB}
            >
              {props.pickingEnd ? p.loopPickEnd : p.loopAB}
            </button>
            {props.fragmentLabel && (
              <button onClick={props.onFragment}>{p.fragment(props.fragmentLabel)}</button>
            )}
          </>
        )}
        <button onClick={props.onComfortable}>{p.comfortable(props.speedStep)}</button>
      </div>

      <div className={styles.secondary}>
        <button
          aria-pressed={props.metronome}
          className={props.metronome ? undefined : styles.off}
          onClick={props.onMetronome}
        >
          {props.metronome ? p.metronomeOn : p.metronomeOff}
        </button>
      </div>
    </div>
  )
}
